const MONTH_NAMES = [
  "", "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

function fmt(n) {
  if (n == null) return "—";
  return typeof n === "number" ? n.toLocaleString("es-ES") : n;
}

function fmtMoney(n) {
  if (n == null) return "—";
  return `${Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €`;
}

export default function MetricsTab({ monthlyData, finances, config }) {
  const hasData = monthlyData.length > 0;
  const hasFinances = finances.length > 0;
  const hasConfig = config && Object.keys(config).length > 0;

  if (!hasData && !hasFinances && !hasConfig) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Este alumno no tiene métricas registradas</p>
      </div>
    );
  }

  // Group finances by year-month
  const financesByPeriod = finances.reduce((acc, f) => {
    const key = `${f.year}-${f.month}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Objectives */}
      {hasConfig && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Objetivos configurados
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {config.capacidad_objetivo != null && (
              <div>
                <span className="text-gray-500">Capacidad</span>
                <p className="font-medium">{config.capacidad_objetivo}</p>
              </div>
            )}
            {config.leads_objetivo != null && (
              <div>
                <span className="text-gray-500">Leads</span>
                <p className="font-medium">{config.leads_objetivo}</p>
              </div>
            )}
            {config.altas_objetivo != null && (
              <div>
                <span className="text-gray-500">Altas</span>
                <p className="font-medium">{config.altas_objetivo}</p>
              </div>
            )}
            {config.bajas_objetivo != null && (
              <div>
                <span className="text-gray-500">Bajas</span>
                <p className="font-medium">{config.bajas_objetivo}</p>
              </div>
            )}
            {config.ticket_medio_objetivo != null && (
              <div>
                <span className="text-gray-500">Ticket medio</span>
                <p className="font-medium">{fmtMoney(config.ticket_medio_objetivo)}</p>
              </div>
            )}
            {config.churn_objetivo != null && (
              <div>
                <span className="text-gray-500">Churn</span>
                <p className="font-medium">{config.churn_objetivo}%</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly data table */}
      {hasData && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">
              Datos mensuales ({monthlyData.length} meses)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Periodo</th>
                  <th className="px-4 py-2">Clientes</th>
                  <th className="px-4 py-2">Bajas</th>
                  <th className="px-4 py-2">Capacidad</th>
                  <th className="px-4 py-2">Lista espera</th>
                  <th className="px-4 py-2">Ticket medio</th>
                  <th className="px-4 py-2">Ingresos</th>
                  <th className="px-4 py-2">Gastos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyData.map((m) => (
                  <tr key={`${m.year}-${m.month}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {MONTH_NAMES[m.month]} {m.year}
                    </td>
                    <td className="px-4 py-2">{fmt(m.n_clientes)}</td>
                    <td className="px-4 py-2">{fmt(m.bajas)}</td>
                    <td className="px-4 py-2">{fmt(m.capacidad)}</td>
                    <td className="px-4 py-2">{fmt(m.lista_espera)}</td>
                    <td className="px-4 py-2">{fmtMoney(m.ticket_medio)}</td>
                    <td className="px-4 py-2 text-emerald-600">
                      {fmtMoney(m.ingresos)}
                    </td>
                    <td className="px-4 py-2 text-red-600">
                      {fmtMoney(m.gastos)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Finances breakdown */}
      {hasFinances && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">
              Desglose por categoría
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(financesByPeriod).map(([key, items]) => {
              const [year, month] = key.split("-").map(Number);
              const ingresos = items.filter((f) => f.tipo === "ingreso");
              const gastos = items.filter((f) => f.tipo === "gasto");

              return (
                <details key={key} className="group">
                  <summary className="px-4 py-3 cursor-pointer hover:bg-gray-50 flex justify-between items-center">
                    <span className="font-medium text-gray-900">
                      {MONTH_NAMES[month]} {year}
                    </span>
                    <span className="text-xs text-gray-500">
                      {items.length} categorías
                    </span>
                  </summary>
                  <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ingresos.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-emerald-600 mb-1">
                          Ingresos
                        </p>
                        {ingresos.map((f, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-sm py-0.5"
                          >
                            <span className="text-gray-600">{f.categoria}</span>
                            <span className="font-medium">
                              {fmtMoney(f.importe)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {gastos.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-600 mb-1">
                          Gastos
                        </p>
                        {gastos.map((f, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-sm py-0.5"
                          >
                            <span className="text-gray-600">{f.categoria}</span>
                            <span className="font-medium">
                              {fmtMoney(f.importe)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
